import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { traqPackAPI } from '../lib/supabase';

interface AddDeviceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDeviceAdded: () => void;
}

const AddDeviceModal: React.FC<AddDeviceModalProps> = ({ isOpen, onClose, onDeviceAdded }) => {
  const [deviceId, setDeviceId] = useState('');
  const [deviceNickname, setDeviceNickname] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!deviceId.trim()) {
      setError('Device ID (IMEI) is required');
      return;
    }

    try {
      setLoading(true);
      const device = await traqPackAPI.createDevice({
        device_id: deviceId.trim(),
        device_nickname: deviceNickname.trim() || `Device ${deviceId.trim()}`,
      });

      if (device) {
        // Check if this was an existing device (by checking if created_at is older than a few seconds)
        const createdAt = new Date(device.created_at || '');
        const isExisting = (new Date().getTime() - createdAt.getTime()) > 5000; // More than 5 seconds old
        
        if (isExisting) {
          setError(`A device with IMEI ${deviceId} already exists. Current nickname: ${device.device_nickname}`);
        } else {
          onDeviceAdded();
          onClose();
          setDeviceId('');
          setDeviceNickname('');
        }
      } else {
        setError('Failed to create device. Please try again.');
      }
    } catch (error) {
      console.error('Error creating device:', error);
      setError('An error occurred while creating the device');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Use Portal to render outside the map's DOM tree
  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4 modal-overlay">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      
      <div className="relative bg-white rounded-2xl p-8 w-full max-w-md shadow-xl modal-content">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-black/5 rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-2xl font-bold text-black mb-6">Add New Device</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="device-id" className="block text-sm font-medium text-black/70 mb-2">
              Device ID (IMEI) *
            </label>
            <input
              id="device-id"
              type="text"
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              placeholder="Enter device IMEI"
              className="w-full px-4 py-3 bg-black/5 rounded-xl border-2 border-transparent focus:border-traqpack-yellow focus:bg-white transition-all duration-200"
              required
            />
          </div>

          <div>
            <label htmlFor="device-nickname" className="block text-sm font-medium text-black/70 mb-2">
              Device Nickname
            </label>
            <input
              id="device-nickname"
              type="text"
              value={deviceNickname}
              onChange={(e) => setDeviceNickname(e.target.value)}
              placeholder="Enter a friendly name (optional)"
              className="w-full px-4 py-3 bg-black/5 rounded-xl border-2 border-transparent focus:border-traqpack-yellow focus:bg-white transition-all duration-200"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-black/60 hover:text-black font-medium transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-traqpack-yellow text-black font-bold rounded-xl hover:bg-traqpack-yellow/90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Adding...' : 'Add Device'}
            </button>
          </div>
        </form>

        <div className="mt-6 p-4 bg-traqpack-yellow/10 rounded-xl">
          <p className="text-sm text-black/70">
            <strong>Note:</strong> Devices are also automatically discovered when they send GPS data for the first time.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddDeviceModal;